import { getFileUploadUrl, uploadFileToS3 } from '@/shared/lib/file-upload';

export interface IntakeFileResult {
  file: File;
  asset: IntakeUploadedAsset | null;
  error: Error | null;
}

export interface IntakeUploadedAsset {
  assetId: string;
  mimeType: string;
  metadata: {
    fileName: string;
    size: number;
  };
}

const toError = (cause: unknown): Error =>
  cause instanceof Error ? cause : new Error(String(cause));

const uploadOneFile = async (file: File, signal: AbortSignal): Promise<IntakeFileResult> => {
  try {
    const { url, fields, assetId } = await getFileUploadUrl(file.type, file.name);
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append('file', file);
    await uploadFileToS3(url, formData, signal);
    return {
      file,
      asset: {
        assetId,
        mimeType: file.type || 'application/octet-stream',
        metadata: {
          fileName: file.name,
          size: file.size,
        },
      },
      error: null,
    };
  } catch (cause) {
    const error = toError(cause);
    if (error.name === 'AbortError') {
      throw error;
    }
    return { file, asset: null, error };
  }
};

export const uploadIntakeFiles = async (
  files: File[],
  signal: AbortSignal,
): Promise<IntakeFileResult[]> => {
  if (files.length === 0) {
    return [];
  }
  return Promise.all(
    files.map(async (file) => {
      try {
        return await uploadOneFile(file, signal);
      } catch (cause) {
        const error = toError(cause);
        throw error;
      }
    }),
  );
};
